import React from 'react';
import { useApolloClient, useMutation } from '@apollo/react-hooks';
import { shareNodeMutation } from '../mutations';
import { useFeatureFlagState } from 'context';
import {
  NodeFragmentWithGroups,
  NodeFragmentWithoutGroups,
} from '../fragments/activityFeedFragment';
import produce from 'immer';
import { toast } from 'react-toastify';
import { SharingToast } from 'components/shared';
import { useTranslation } from 'hooks';
import { errorMessage, trackError } from 'helpers/error';
import { getFlag, flags } from 'services';

function waitForXSeconds(seconds: number) {
  var promise = new Promise(function (resolve, reject) {
    window.setTimeout(function () {
      resolve();
    }, seconds * 1000);
  });
  return promise;
}

export const useShareToPerson = () => {
  const client = useApolloClient();
  const [shareNode] = useMutation(shareNodeMutation);
  const { groupsFeatureFlag } = useFeatureFlagState();
  const { t } = useTranslation();

  const shareNodeToPerson = (
    nodeId: string,
    targetId: string,
    avatarUrl: string,
    isOrgMember: boolean,
    name: string,
    email: string
  ) => {
    // Step 1. Get node from cache
    const readNode: any = client.readFragment({
      id: nodeId,
      fragment: groupsFeatureFlag ? NodeFragmentWithGroups : NodeFragmentWithoutGroups,
    });

    // Step 2. Remove person from shareTargets
    const shareTargetToModify = isOrgMember
      ? readNode.shareTargets.orgPeopleTargets.targets
      : readNode.shareTargets.otherPeopleTargets.targets;

    const newShareTargets = produce(shareTargetToModify, (draft: any) => {
      const index = draft.findIndex((target: any) => target.id === targetId);
      if (index !== -1) draft.splice(index, 1);
    });

    // Step 3. Generate updated fragment
    const removeShareTargetFromCache = produce(readNode, (draft: any) => {
      isOrgMember
        ? (draft.shareTargets.orgPeopleTargets.targets = newShareTargets)
        : (draft.shareTargets.otherPeopleTargets.targets = newShareTargets);
    });

    // Step 4. Update Cache
    client.writeFragment({
      id: nodeId,
      fragment: groupsFeatureFlag ? NodeFragmentWithGroups : NodeFragmentWithoutGroups,
      data: removeShareTargetFromCache,
    });

    waitForXSeconds(0.875).then(() => {
      // Step 5. Read node again
      const readNodeAgain: any = client.readFragment({
        id: nodeId,
        fragment: groupsFeatureFlag ? NodeFragmentWithGroups : NodeFragmentWithoutGroups,
      });

      // Step 6. Add person to Accessors
      const currentAccessors = readNodeAgain.accessorInfo.accessors;
      const updatedAccessors = produce(currentAccessors, (draft: any) => {
        const newNode = {
          id: targetId,
          name: name,
          avatarUrl: avatarUrl,
          email: email ? email : 'unkown',
          __typename: 'UserProfile',
        };
        if (currentAccessors?.length === 0) {
          draft[currentAccessors.length] = newNode;
        } else if (currentAccessors?.length === 1) {
          draft[1] = currentAccessors[0];
          draft[0] = newNode;
        } else if (currentAccessors?.length >= 2) {
          draft[2] = currentAccessors[1];
          draft[1] = currentAccessors[0];
          draft[0] = newNode;
        }
      });

      // Step 7. Generate updated fragment
      const addAccessorToCache = produce(readNodeAgain, (draft: any) => {
        draft.accessorInfo.accessors = updatedAccessors;
        draft.accessorInfo.pageInfo.total = readNodeAgain.accessorInfo.pageInfo.total + 1;
      });

      // Step 8. Update Cache to add accessor
      client.writeFragment({
        id: nodeId,
        fragment: groupsFeatureFlag ? NodeFragmentWithGroups : NodeFragmentWithoutGroups,
        data: addAccessorToCache,
      });

      if (getFlag(flags.FeedPlusButtonMutation)) {
        shareNode({
          variables: {
            id: nodeId,
            targetId: targetId,
          },
          update: (cache, response) => {
            if (!response.data.shareNode.ok) {
              trackError(errorMessage.FailedToShare, response.data.shareNode.error);
              toast(
                <SharingToast
                  message={t('toasts.sharing-error')}
                  nodeId={nodeId}
                  groupsEnabled={groupsFeatureFlag}
                  hasCloseButton
                />,
                {
                  position: toast.POSITION.BOTTOM_LEFT,
                  hideProgressBar: true,
                  closeButton: true,
                  closeOnClick: true,
                  autoClose: false,
                }
              );
            }
          },
        });
      }
    });

    return;
  };

  return [shareNodeToPerson];
};

export const useShareToGroup = () => {
  const client = useApolloClient();
  const [shareNode] = useMutation(shareNodeMutation);
  const { groupsFeatureFlag } = useFeatureFlagState();
  const { t } = useTranslation();

  const shareNodeToGroup = (nodeId: string, targetId: string, groupName: string) => {
    // Step 1. Get node from cache
    const readNode: any = client.readFragment({
      id: nodeId,
      fragment: groupsFeatureFlag ? NodeFragmentWithGroups : NodeFragmentWithoutGroups,
    });

    // Step 2. Remove group from shareTargets
    const newShareTargets = produce(readNode.shareTargets.groupTargets.targets, (draft: any) => {
      const index = draft.findIndex((target: any) => target.id === targetId);
      if (index !== -1) draft.splice(index, 1);
    });

    // Step 3. Generate updated fragment
    const removeShareTargetFromCache = produce(readNode, (draft: any) => {
      draft.shareTargets.groupTargets.targets = newShareTargets;
    });

    // Step 4. Update Cache
    client.writeFragment({
      id: nodeId,
      fragment: groupsFeatureFlag ? NodeFragmentWithGroups : NodeFragmentWithoutGroups,
      data: removeShareTargetFromCache,
    });

    waitForXSeconds(0.875).then(() => {
      // Step 5. Read node again
      const readNodeAgain: any = client.readFragment({
        id: nodeId,
        fragment: groupsFeatureFlag ? NodeFragmentWithGroups : NodeFragmentWithoutGroups,
      });

      // Step 6. Add group to Accessors
      const currentAccessors = readNodeAgain.accessorInfo.accessors;
      const updatedAccessors = produce(currentAccessors, (draft: any) => {
        const newNode = {
          id: targetId,
          groupName: groupName,
          __typename: 'Group',
        };
        if (currentAccessors?.length === 0) {
          draft[currentAccessors.length] = newNode;
        } else if (currentAccessors?.length === 1) {
          draft[1] = currentAccessors[0];
          draft[0] = newNode;
        } else if (currentAccessors?.length >= 2) {
          draft[2] = currentAccessors[1];
          draft[1] = currentAccessors[0];
          draft[0] = newNode;
        }
      });

      // Step 7. Generate updated fragment
      const addAccessorToCache = produce(readNodeAgain, (draft: any) => {
        draft.accessorInfo.accessors = updatedAccessors;
        draft.accessorInfo.pageInfo.total = readNodeAgain.accessorInfo.pageInfo.total + 1;
      });

      // Step 8. Update Cache to add accessor
      client.writeFragment({
        id: nodeId,
        fragment: groupsFeatureFlag ? NodeFragmentWithGroups : NodeFragmentWithoutGroups,
        data: addAccessorToCache,
      });

      if (getFlag(flags.FeedPlusButtonMutation)) {
        shareNode({
          variables: {
            id: nodeId,
            targetId: targetId,
          },
          update: (cache, response) => {
            if (!response.data.shareNode.ok) {
              trackError(errorMessage.FailedToShare, response.data.shareNode.error);
              toast(
                <SharingToast
                  message={t('toasts.sharing-error')}
                  nodeId={nodeId}
                  groupsEnabled={groupsFeatureFlag}
                  hasCloseButton
                />,
                {
                  position: toast.POSITION.BOTTOM_LEFT,
                  hideProgressBar: true,
                  closeButton: true,
                  closeOnClick: true,
                  autoClose: false,
                }
              );
            }
          },
        });
      }
    });

    return;
  };
  return [shareNodeToGroup];
};
